import _ from 'lodash';
import { InjectableClass } from '@fino/lib-injection';
import { FinanceTypes } from '../confirmFinances/confirmFinances.constants';
import { FeatureFlagNames } from '../../featureFlags/featureFlags';

export class CardService extends InjectableClass {
  constructor(
    $q,
    $timeout,
    uiModalDeclarationService,
    cardBuildingService,
    CardModel,
    creditorsService,
    ticketService,
    featureFlagService,
  ) {
    'ngInject';
    super({
      $q,
      $timeout,
      uiModalDeclarationService,
      cardBuildingService,
      CardModel,
      creditorsService,
      ticketService,
      featureFlagService,
    });

    this.contractingPartners = [];
    this.editCallbacks = [];
    this.editIndex = -1;
    this.resetCards();
  }

  openCard(card) {
    return this.uiModalDeclarationService.$uibModal
      .open({
        component: 'paymentPartnerEditing',
        size: 'lg',
        resolve: {
          card: () => {
            return card;
          },
        },
      })
      .result.then(
        (card) => {
          if (card.name) {
            if (card.isAdded) {
              card.header = card.partner.name;
            }
            card.subHeader = card.partner.name;
          }
          card.error = this.cardError(card);
          return card;
        },
        (card) => {
          card.error = this.cardError(card);
          return card;
        },
      );
  }

  cancelContractingPartner(card) {
    return this.uiModalDeclarationService.$uibModal
      .open({
        component: 'contractPartnerCancellation',
        size: 'lg',
        resolve: {
          card: () => {
            return card;
          },
        },
      })
      .result.then(
        (card) => {
          if (card.name) {
            if (card.isAdded) {
              card.header = card.partner.name;
            }
            card.subHeader = card.partner.name;
          }
          card.error = this.cardError(card);
          return card;
        },
        (card) => {
          card.error = this.cardError(card);
          return card;
        },
      );
  }

  edit(cp) {
    _.forEach(this.editCallbacks, (e) => e(cp));
  }

  onEdit(callback) {
    this.editCallbacks.push(callback);
  }

  resetCards() {
    this.cards = [];
  }

  createCard(type, ticketId, index, additionalData = null) {
    const card = angular.copy(this.CardModel);
    _.assign(
      card,
      {
        id: index,
        informPartnerMethod: type === 'directDebits' ? 'automatic' : 'manual',
        ticketId,
        type,
      },
      additionalData || {},
    );

    return card;
  }

  buildPaymentPartnerCard(partner, options = {}) {
    const card = this.cardBuildingService
      .buildFor(partner)
      .withHeader('name')
      .withSubHeader('partner.name')
      // .withIconClass('category')
      .isRemovable('removed')
      .isReadonly(!this.ticketService.canHandleTicket())
      .withInformOnlineStatus('partner.isOnline')
      .withContractingPartners('contractingPartner')
      .onChange((c) => {})
      .return();

    // Check if feature flag "groupMultipleContracts" is active
    if (
      this.featureFlagService.isFeatureActive(
        FeatureFlagNames.FF_GROUP_MULTIPLE_CONTRACTS,
      )
    ) {
      // Check if given payment partner has multiple contracts
      this.cardBuildingService.hasMultipleContracts();
    }

    if (!options.ignore) {
      this.cards.push(card);
    }

    return card;
  }

  findCreditorsSearch(model, limit = 10) {
    const { creditorsService } = this;
    const { searchText } = model.options;

    // Reset search result filter
    model.card.filtered = null;
    // The found creditors will be added to the
    // suggested partners list of this card.
    if (!model.card.suggestedPartners) {
      model.card.suggestedPartners = [];
    }

    // Reset suggestedPartners when no search value is given
    if (!searchText) {
      model.card.searchResults = null;
      return;
    }

    return creditorsService.find(searchText, limit).then((creditors) => {
      // Tag all creditors with searched flag
      model.card.searchResults = creditors;
    });
  }

  hasAddressData(data, ignoreMethod = false) {
    const isAddressRequired = ignoreMethod || this.isAddressRequired(data);
    const { city, name, fax } = data.partner;

    // Data are complete either no address is required
    // or when fax or full address is given.
    return !!(
      !isAddressRequired ||
      (city && name) || // 27-06-17 Removed address as required field [KWH-1008]
      (fax && name)
    );
  }

  isAddressRequired(data) {
    return data.informPartnerMethod === 'automatic';
  }

  prepareDataForRequest(data) {
    return _.reduce(
      data,
      (result, value, key) => {
        result[key] = _.map(value, (item) => {
          // Mark removed items (cards) with 'ignoreOnPrint' flag
          item.ignoreOnPrint = item.removable && item.removable.removed;

          // Map contract partner and comment
          item.affiliate = this.createAffiliateFromPartner(
            item.contractingPartner,
          );
          item.e2eref = item.comment;

          return item;
        });
        return result;
      },
      {},
    );
  }

  prepareDirectDebit(model) {
    model.type = FinanceTypes.directDebits.type;

    // Add active flag and parse amount to number for all contracts
    if (model.contracts) {
      model.contracts.forEach((c) => {
        c.active = true;
        c.amount = this.parseAmount(c.amount);
      });
    }
  }

  prepareReceivedPayment(model) {
    model.type = FinanceTypes.receivedPayments.type;

    // Add active flag and parse amount to number for all contracts
    if (model.contracts) {
      model.contracts.forEach((c) => {
        c.active = true;
        c.amount = this.parseAmount(c.amount);
      });
    }

    // We set the informPartnerMethod to 'manual' for received payments
    // with incomplete address data.
    if (!this.hasAddressData(model)) {
      model.informPartnerMethod = 'manual';
    }
  }

  // seems redundant, but numbers are captured by humans
  // so combinations like 222.111,23   2332.21   and 221,21 can happen
  parseAmount(amount) {
    if (!amount) return '';
    if (amount.charAt(amount.length - 3) === ',' && !amount.includes('.')) {
      return parseFloat(`${amount}`.replace(',', '.'));
    } else {
      return parseFloat(`${amount}`.replaceAll('.', '').replace(',', '.'));
    }
  }

  prepareStandingOrder(model) {
    model.type = FinanceTypes.standingOrders.type;
  }

  createAffiliateFromPartner(partner) {
    if (!partner) return;
    const { firstName, lastName } = partner;
    const affiliate = partner;
    affiliate.name = `${firstName} ${lastName}`;
    return affiliate;
  }

  cardIsRemoved(card) {
    return card.removable && card.removable.removed;
  }

  cardError(data) {
    let addressMissing = !this.hasAddressData(data);
    let addressSuggested = false;

    // Adjust address error when there are address suggestions available
    const numSuggestions =
      data.suggestedPartners && data.suggestedPartners.length;
    if (addressMissing && numSuggestions) {
      // Swap address missing with suggested error
      addressMissing = false;
      addressSuggested = numSuggestions;
    }

    return {
      hasError: addressMissing || addressSuggested,
      fields: {
        // Marks the 'address' section as errored
        address: addressMissing || addressSuggested,

        // Handle the displayed error text
        addressMissing,
        addressSuggested,
      },
    };
  }

  cardHasError(card) {
    return card.error && card.error.hasError;
  }

  allCardsValid() {
    return !_.some(
      this.cards,
      (c) => !this.cardIsRemoved(c) && this.cardHasError(c),
    );
  }

  editContractingPartner(cp) {
    this.edit(angular.copy(cp));
    return this.uiModalDeclarationService.open('contractingPartners');
  }

  addContractingPartner(contractingPartner) {
    let matchingContractingPartner = _.find(
      this.contractingPartners,
      (cp) => cp.id === contractingPartner.id,
    );
    if (matchingContractingPartner) {
      return _.assign(matchingContractingPartner, contractingPartner);
    }
    this.contractingPartners.push(
      this.createContractingPartner(contractingPartner),
    );
  }

  clearContractingPartners() {
    this.contractingPartners = [];
  }

  createContractingPartner(data = {}) {
    return {
      address: data.address || null,
      city: data.city || null,
      firstName: data.firstName || null,
      lastName: data.lastName || null,
      name: data.name || null,
      postcode: data.postcode || null,
      id: data.id || Math.random(),
    };
  }
}
